

import type React from "react"
import { useEffect, useState, useRef, useMemo } from "react"
import {
  Box,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  Tooltip,
  Dialog,
  DialogContent,
  Autocomplete,
  CircularProgress,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { Add } from "@mui/icons-material"
import dayjs from "dayjs"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PathologyPatientRegistration from "../settings/patientReg"
import PatientSearchDialog from "../patientSearchDialog"
import CollectionCenterDropdown from "../../../components/CollectionCenterDropdown"
import { fetchProfileData } from "../../../redux/reducers/profileSlice"
import axios from "axios"
import BillReport from "../reports/billReport"
import ThermalBillReport from "../reports/billReport/thermal"
import { RequestSource } from "../../../enums"

interface PatientData {
  _id: string
  mrn?: string | number
  patientName: string
  guardiansName: string
  gender: string
  dob: string
  phonNumber: string
  cnic?: string
  healthId?: string
  city: string
  reference?: string
  patientType?: string
  createdAt?: string // Added for sorting by recency
}

interface RegisterFormProps {
  onNext: () => void
  setSelectedId: any
  formData: PatientData
  getId: (args: string) => void
  storeData: any
  selectedId: any
}

interface FormData {
  patientTypeId?: string
  patientType?: string
  collectionDate?: string
  reportingDate?: string
  patientId?: string
  testToPerform?: {
    testId: string
    sampleType: string
    reportingTime: string
    sampleReceived: boolean
    collectionType: string
    sampleCollectionDateTime?: any
    status?: string
  }[]
  paymentDetail?: {
    grossAmount: number
    discountInPercentage: number
    discountInAmount: number
    nowPaying: number
    netAmount?: number
    dueAmount: number
  }
  otherDetails?: {
    [key: string]: any
  }
  collectionCenterId?: string
}

interface PatientType {
  _id: string
  patientTypeName: string
}

interface ReferringDoctorOption {
  _id: string
  doctorName: string
  specialization?: string
  hospitalClinic?: string
  referralCommission?: number
}

/** "Self" is the default referring doctor and is not part of the master data. */
const SELF_REFERRING_DOCTOR_ID = "__self__"
const SELF_REFERRING_DOCTOR_LABEL = "Self"
/** Placeholder for a free-text doctor saved before the master data existed. */
const LEGACY_REFERRING_DOCTOR_ID = "__legacy__"

const SELF_REFERRING_DOCTOR: ReferringDoctorOption = {
  _id: SELF_REFERRING_DOCTOR_ID,
  doctorName: SELF_REFERRING_DOCTOR_LABEL,
}

const isSelfReferredBy = (referredBy?: string) =>
  !referredBy || referredBy.trim().toLowerCase() === SELF_REFERRING_DOCTOR_LABEL.toLowerCase()

/**
 * Payment amounts must stay fully readable, including in the read-only fields where
 * MUI would otherwise dim the text to rgba(0, 0, 0, 0.38).
 */
const paymentFieldSx = {
  "& .MuiInputBase-input": {
    fontSize: "15px",
    fontWeight: 700,
    color: "#000",
    WebkitTextFillColor: "#000",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#000",
    WebkitTextFillColor: "#000",
    opacity: 1,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#000",
    opacity: 1,
  },
}

const CollectionForm: React.FC<RegisterFormProps> = () => {
  const dispatch = useDispatch()
  const profile = useSelector((state: any) => state.profileSlice.profile)
  const labPermissions = profile?.data?.accessConfigurations?.Lab || {}
  const location = useLocation()
  const { patient, TestData, edit } = location.state || {}
  const isEditMode = useRef<boolean>(!!TestData?.patientTypeId)
  const testSearchInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState<boolean>(false)
  const [searchType, setSearchType] = useState("MRN")
  const [searchValue, setSearchValue] = useState("")
  const [openSearchList, setOpenSearchList] = useState(false)
  const [searchResults, setSearchResults] = useState<PatientData[]>([])
  const [testList, setTestList] = useState<any[]>([])
  const [selectedTests, setSelectedTests] = useState<any[]>([])
  const [patientType, setPatientType] = useState<PatientType[]>([])
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [searchTestByCode, setSearchTestByCode] = useState<string>("")
  const [sampleReceived, setSampleReceived] = useState<boolean>(false)
  const [latestPatient, setLatestPatient] = useState<PatientData[]>([])
  const [BillPrintLoading, setBillPrintLoading] = useState<boolean>(false)
  const [billData, setBillData] = useState<any>(null)
  const [openPrintDialogPayment, setOpenPrintDialogPayment] = useState<boolean>(false)
  const [printFormat, setPrintFormat] = useState<"a4" | "thermal">("a4")
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>({
    _id: patient?._id || "" || "",
    patientName: "",
    gender: "",
    dob: "",
    phonNumber: "",
    city: "",
    patientType: "",
    guardiansName: "",
    reference: "",
    createdAt: "",
  })
  const [collectionTypes, setCollectionTypes] = useState<any[]>([])
  const [referringDoctors, setReferringDoctors] = useState<ReferringDoctorOption[]>([])
  const navigate = useNavigate()

  const [errors, setErrors] = useState<{ [key: string]: string }>({
    collectionDate: "",
    reportingDate: "",
    patientTypeId: "",
    testToPerform: "",
    patientId: "",
    collectionCenterId: "",
  })

  const [formData, setFormData] = useState<FormData>({
    patientId: patient?._id || selectedPatient?._id || "",
    patientTypeId: TestData?.patientTypeId || "",
    collectionDate: TestData?.collectionDate || dayjs().format("YYYY-MM-DDTHH:mm"),
    reportingDate: TestData?.ReportingDate || dayjs().format("YYYY-MM-DDTHH:mm"),
    testToPerform: [],
    paymentDetail: {
      grossAmount: TestData?.paymentDetail?.grossAmount || 0,
      discountInPercentage: TestData?.paymentDetail?.discountInPercentage || 0,
      discountInAmount: TestData?.paymentDetail?.discountInAmount || 0,
      nowPaying: TestData?.paymentDetail?.nowPaying || 0,
      netAmount: TestData?.paymentDetail?.netAmount || 0,
      dueAmount: TestData?.paymentDetail?.dueAmount || 0,
    },
    otherDetails: {
      specimenType: TestData?.otherDetails?.specimenType || "",
      externalRefNo: TestData?.otherDetails?.externalRefNo || "",
      priority: TestData?.otherDetails?.priority || "Normal",
      clinicalNotes: TestData?.otherDetails?.clinicalNotes || "",
      referredBy: TestData?.otherDetails?.referredBy || SELF_REFERRING_DOCTOR_LABEL,
      referringDrId: TestData?.otherDetails?.referringDrId || null,
      referralCommission: TestData?.otherDetails?.referralCommission ?? 0,
      requestSource: TestData?.otherDetails?.requestSource || "",
    },
    collectionCenterId: TestData?.collectionCenterId || "",
  })

  useEffect(() => {
    if (TestData?.testToPerform) {
      setSelectedTests(
        TestData?.testToPerform?.map((test: any) => ({
          ...test.test,
        })),
      )
      setFormData((prev) => ({
        ...prev,
        testToPerform: [
          ...(TestData?.testToPerform?.map((test: any) => ({
            testId: test.testId,
            sampleType: test.sampleType,
            reportingTime: test.reportingTime,
            sampleReceived: test.sampleReceived,
            collectionType: test.collectionType,
            sampleCollectionDateTime: test.sampleCollectionDateTime
              ? test.sampleCollectionDateTime
              : test.sampleReceived
                ? dayjs().format("YYYY-MM-DDTHH:mm")
                : null,
            status: test.status,
            charges: test.charges,
          })) || []),
        ],
      }))
    }
  }, [TestData])
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      testToPerform: prev.testToPerform?.map((test) => ({
        ...test,
        sampleCollectionDateTime: test.sampleReceived ? test.sampleCollectionDateTime : null,
      })),
    }))
  }, [sampleReceived])

  useEffect(() => {
    if (patient?._id && !selectedPatient?._id) {
      setFormData((prev) => ({
        ...prev,
        patientId: patient._id,
      }))
      setSelectedPatient(patient)
    }
  }, [patient?._id, patient, selectedPatient?._id])

  // Handle payment
  useEffect(() => {
    const grossAmount = Math.round(
      selectedTests.reduce((sum, test) => sum + Number(test.testCharges ?? test.charges ?? 0), 0),
    )
    setFormData((prev) => {
      if (grossAmount === 0 && (prev.paymentDetail?.grossAmount ?? 0) > 0) {
        return prev
      }
      return {
        ...prev,
        paymentDetail: {
          grossAmount,
          discountInPercentage: prev.paymentDetail?.discountInPercentage ?? 0,
          discountInAmount: prev.paymentDetail?.discountInAmount ?? 0,
          nowPaying: prev.paymentDetail?.nowPaying ?? 0,
          netAmount: grossAmount - (prev.paymentDetail?.discountInAmount ?? 0),
          dueAmount: Math.max(
            0,
            grossAmount - (prev.paymentDetail?.discountInAmount ?? 0) - (prev.paymentDetail?.nowPaying ?? 0),
          ),
        },
      } as FormData
    })
  }, [selectedTests])

  // Calculate discount amount when percentage changes
  useEffect(() => {
    if (formData.paymentDetail) {
      const { grossAmount, discountInPercentage, nowPaying } = formData.paymentDetail
      const calculatedDiscountAmount = Math.round((grossAmount * discountInPercentage) / 100)
      const netAmount = grossAmount - calculatedDiscountAmount
      const calculatedDueAmount = Math.max(0, netAmount - nowPaying)

      setFormData((prev) => ({
        ...prev,
        paymentDetail: {
          ...prev.paymentDetail!,
          discountInAmount: calculatedDiscountAmount,
          netAmount: netAmount,
          dueAmount: calculatedDueAmount,
        },
      }))
    }
    // </CHANGE> Fixed dependency array to use the whole paymentDetail object instead of specific properties
  }, [formData.paymentDetail])

  const getBillData = async ({ id }: { id: any }) => {
    if (!id) return
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/patient-result/getPaymentDetails/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (response.status === 200) {
        setBillData(response?.data?.data)
        setOpenPrintDialogPayment(true)
      }
    } catch (error: any) {
      console.error("Error fetching patient types:", error)
      toast.error(error.message || "Error fetching bill data")
    }
  }
  // Calculate discount percentage when amount changes
  const handleDiscountAmountChange = (discountAmount: number) => {
    const grossAmount = formData.paymentDetail?.grossAmount || 0
    const validDiscountAmount = Math.min(discountAmount, grossAmount) // Cap at gross amount
    const calculatedPercentage = grossAmount > 0 ? (validDiscountAmount / grossAmount) * 100 : 0
    const nowPaying = formData.paymentDetail?.nowPaying || 0
    const netAmount = grossAmount - validDiscountAmount
    const calculatedDueAmount = Math.max(0, netAmount - nowPaying)

    setFormData((prev) => ({
      ...prev,
      paymentDetail: {
        ...prev.paymentDetail!,
        discountInAmount: validDiscountAmount,
        discountInPercentage: Math.min(100, Math.max(0, calculatedPercentage)),
        dueAmount: calculatedDueAmount,
      },
    }))
  }

  // Calculate due amount when now paying changes
  const handleNowPayingChange = (nowPaying: number) => {
    const grossAmount = formData.paymentDetail?.grossAmount || 0
    const discountAmount = formData.paymentDetail?.discountInAmount || 0
    const netAmount = grossAmount - discountAmount
    const validNowPaying = Math.min(nowPaying, netAmount)
    const calculatedDueAmount = Math.max(0, netAmount - validNowPaying)

    setFormData((prev) => ({
      ...prev,
      paymentDetail: {
        ...prev.paymentDetail!,
        nowPaying: validNowPaying,
        dueAmount: calculatedDueAmount,
      },
    }))
  }

  // Handle age calculation
  function calculateAge(dob: string | Date) {
    const birthDate = new Date(dob)
    const today = new Date()

    let years = today.getFullYear() - birthDate.getFullYear()
    let months = today.getMonth() - birthDate.getMonth()
    let days = today.getDate() - birthDate.getDate()

    if (days < 0) {
      months--
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate()
    }
    if (months < 0) {
      years--
      months += 12
    }

    return { yy: years, mm: months, dd: days }
  }

  function toDays(a: { yy: number; mm: number; dd: number }) {
    return a.yy * 365 + a.mm * 30 + a.dd
  }

  function isWithinRange(age: { yy: number; mm: number; dd: number }, from: any, to: any) {
    const ageDays = toDays(age)
    return ageDays >= toDays(from) && ageDays <= toDays(to)
  }

  function filterPatientTypes(dob: string, data: any[]) {
    const age = calculateAge(dob)
    return data.filter((item) => isWithinRange(age, item.ageFrom, item.ageTo))
  }

  useEffect(() => {
    if (!patient?.dob || !patientType || patientType.length === 0) return

    // Don't auto-select if we're in edit mode
    if (isEditMode.current) return

    const results = filterPatientTypes(patient.dob, patientType)
    let selected: any = null

    if (results.length === 1) {
      selected = results[0]
    } else if (results.length > 1) {
      if (patient?.gender === "Male") {
        selected = results.find((pt) => pt.patientTypeName.toLowerCase() === "male")
      } else if (patient?.gender === "Female") {
        selected = results.find((pt) => pt.patientTypeName.toLowerCase() === "female")
      }
    }
    setFormData((prev) => ({
      ...prev,
      patientTypeId: selected ? selected._id : "",
    }))
  }, [patient?.dob, patient?.gender, patientType, isEditMode.current, patient]) // Added patient as dependency

  const handleSearchTypeChange = (event: any) => {
    setSearchType(event.target.value)
    setSearchValue("")
  }

  const validateForm = () => {
    let isValid = true
    const newErrors: { [key: string]: string } = {}

    if (!formData.patientId) {
      newErrors.patientId = "Patient ID is required"
      isValid = false
    }
    if (!formData.patientTypeId) {
      newErrors.patientTypeId = "Patient Type is required"
      isValid = false
    }
    if (!formData.testToPerform || formData.testToPerform.length === 0) {
      newErrors.testToPerform = "At least one test is required"
      isValid = false
    }
    if (!formData.collectionDate) {
      newErrors.collectionDate = "Collection Date is required"
      isValid = false
    }
    if (!formData.reportingDate) {
      newErrors.reportingDate = "Reporting Date is required"
      isValid = false
    }
    if (!formData.collectionCenterId) {
      newErrors.collectionCenterId = "Collection Center is required"
      isValid = false
      toast.error("Please select Collection Center")
    }

    console.log(newErrors, "errors")
    setErrors(newErrors)
    return isValid
  }

  const handleSaveTest = async () => {
    if (!validateForm()) {
      return
    }
    if (!formData.patientId) {
      toast.error("Please select a patient from the search list")
      return
    }

    setLoading(true)
    try {
      const dataToSend = {
        ...formData,
        collectionDate: formData.collectionDate ? dayjs(formData.collectionDate).toISOString() : null,
        reportingDate: formData.reportingDate ? dayjs(formData.reportingDate).toISOString() : null,
        testToPerform: formData.testToPerform?.map((test) => ({
          ...test,
          reportingTime: test.reportingTime ? dayjs(test.reportingTime).toISOString() : null,
          sampleCollectionDateTime: test.sampleCollectionDateTime
            ? dayjs(test.sampleCollectionDateTime).toISOString()
            : null,
        })),
      }
      const response = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/sample-collection/createSampleCollection`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      )
      if (response.data?.isSuccess === true) {
        toast.success(response.data?.message || "Test saved successfully")
        setLoading(false)
        navigate("/sample")
      } else {
        toast.error("Failed to save test")
        setLoading(false)
      }
    } catch (error: any) {
      setLoading(false)
      console.error("Error saving test:", error)
      toast.error(error.message)
    }
  }
  const handleSaveTestAndPrint = async (format: "a4" | "thermal" = "a4") => {
    if (!validateForm()) {
      return
    }
    if (!formData.patientId) {
      toast.error("Please select a patient from the search list")
      return
    }

    setPrintFormat(format)
    setBillPrintLoading(true)
    try {
      const dataToSend = {
        ...formData,
        collectionDate: formData.collectionDate ? dayjs(formData.collectionDate).toISOString() : null,
        reportingDate: formData.reportingDate ? dayjs(formData.reportingDate).toISOString() : null,
        testToPerform: formData.testToPerform?.map((test) => ({
          ...test,
          reportingTime: test.reportingTime ? dayjs(test.reportingTime).toISOString() : null,
          sampleCollectionDateTime: test.sampleCollectionDateTime
            ? dayjs(test.sampleCollectionDateTime).toISOString()
            : null,
        })),
      }
      const response = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/sample-collection/createSampleCollection`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      )
      if (response.data?.isSuccess === true) {
        getBillData({ id: response.data?.data?._id })
        toast.success(response.data?.message || "Test saved successfully")
        setBillPrintLoading(false)
      } else {
        toast.error("Failed to save test")
        setLoading(false)
      }
    } catch (error: any) {
      setBillPrintLoading(false)
      console.error("Error saving test:", error)
      toast.error(error?.response.data.message)
    }
  }

  const handleUpdateTest = async () => {
    if (!validateForm()) {
      return
    }
    if (!formData.patientId) {
      toast.error("Please select a patient from the search list")
      return
    }
    setLoading(true)
    try {
      const dataToSend = {
        ...formData,
        collectionDate: formData.collectionDate ? dayjs(formData.collectionDate).toISOString() : null,
        reportingDate: formData.reportingDate ? dayjs(formData.reportingDate).toISOString() : null,
        testToPerform: formData.testToPerform?.map((test) => ({
          ...test,
          reportingTime: test.reportingTime ? dayjs(test.reportingTime).toISOString() : null,
          sampleCollectionDateTime: test.sampleCollectionDateTime
            ? dayjs(test.sampleCollectionDateTime).toISOString()
            : null,
        })),
      }
      const response = await axios.patch(
        `${process.env.REACT_APP_LAB_URL}/sample-collection/updateOne/${TestData._id}`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      )
      if (response.data?.isSuccess === true) {
        toast.success(response.data?.message || "Test updated successfully")
        // getBillData({ id: response.data?.data?._id });
        setLoading(false)
        navigate("/sample")
      } else {
        toast.error(response.data?.message || "Failed to update test")
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.error("Error saving test:", error)
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }
  const handleUpdateTestPrintBill = async (format: "a4" | "thermal" = "a4") => {
    if (!validateForm()) {
      return
    }
    if (!formData.patientId) {
      toast.error("Please select a patient from the search list")
      return
    }
    setPrintFormat(format)
    setBillPrintLoading(true)
    try {
      const dataToSend = {
        ...formData,
        collectionDate: formData.collectionDate ? dayjs(formData.collectionDate).toISOString() : null,
        reportingDate: formData.reportingDate ? dayjs(formData.reportingDate).toISOString() : null,
        testToPerform: formData.testToPerform?.map((test) => ({
          ...test,
          reportingTime: test.reportingTime ? dayjs(test.reportingTime).toISOString() : null,
          sampleCollectionDateTime: test.sampleCollectionDateTime
            ? dayjs(test.sampleCollectionDateTime).toISOString()
            : null,
        })),
      }
      const response = await axios.patch(
        `${process.env.REACT_APP_LAB_URL}/sample-collection/updateOne/${TestData._id}`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      )
      if (response.data?.isSuccess === true) {
        toast.success(response.data?.message || "Test updated successfully")
        getBillData({ id: response.data?.data?._id })
        setBillPrintLoading(false)
        // navigate("/sample");
      } else {
        toast.error(response.data?.message || "Failed to update test")
        setLoading(false)
      }
    } catch (error) {
      setBillPrintLoading(false)
      console.error("Error saving test:", error)
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const fetchPatientTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/patient-type/getAllBySearch`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      setPatientType(response.data?.data || [])
    } catch (error: any) {
      console.error("Error fetching patient types:", error)
      toast.error(error?.response.data.message || "Error fetching patient types")
    }
  }

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/tests/getAllLabTestsBySearch`, {
        params: {
          testName: searchQuery,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (response.data?.isSuccess === true) {
        setTestList(response.data?.data || [])
      } else {
        toast.error(response.data?.message)
      }
    } catch (error) {
      console.error("Error fetching tests:", error)
      toast.error("Error fetching tests")
    }
  }

  const fetchTestsByCode = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/tests/getAllLabTestsBySearch`, {
        params: {
          testCode: searchTestByCode,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (response.data?.isSuccess === true) {
        const newTests = (response.data?.data || []).filter((item: any) => {
          const exists = selectedTests.some((p) => p._id === item._id)
          if (exists) {
            toast.info(`Test ${item.testName} is already added`)
            return false
          }
          return true
        })

        setSelectedTests((prev) => [...prev, ...newTests])

        setFormData((prev) => ({
          ...prev,
          testToPerform: [
            ...(prev.testToPerform || []),
            ...newTests.map((newValue: any) => ({
              testId: newValue._id,
              sampleType: "Normal",
              reportingTime: dayjs()
                .add(newValue.ETA?.days || 0, "day")
                .add(newValue.ETA?.hrs || 0, "hour")
                .add(newValue.ETA?.mins || 0, "minute")
                .format("YYYY-MM-DDTHH:mm"),
              sampleReceived: true,
              collectionType: "Taken in lab",
              sampleCollectionDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
            })),
          ],
        }))
      } else {
        toast.error(response.data?.message)
      }
    } catch (error: any) {
      console.error("Error fetching tests:", error)
      toast.error(error?.response.data.message || "Error fetching tests by code")
    }
  }

  const fetchLatestPatient = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/patient-registration/getLabAllPatients`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (response.data?.isSuccess === true) {
        const patients: PatientData[] = response.data?.data || []
        setLatestPatient(patients)

        // Select the latest patient based on createdAt or _id
        if (patients.length > 0) {
          const latest = patients.reduce((latest, current) => {
            const latestDate = new Date(latest.createdAt || 0)
            const currentDate = new Date(current.createdAt || 0)
            return currentDate > latestDate ? current : latest
          }, patients[0])

          // setSelectedPatient(latest);
          // setFormData((prev) => ({
          //   ...prev,
          //   patientId: latest._id,
          // }));
          // navigate("/sample-collection", {
          //   state: { patient: latest, TestData, edit },
          // });
        }
      } else {
        toast.error("Failed to fetch patients")
      }
    } catch (error: any) {
      console.error("Error fetching patients:", error)
      toast.error(error?.response.data.message || "Error fetching patients")
    }
  }

  useEffect(() => {
    fetchTests()
    fetchPatientTypes()
    fetchLatestPatient()
    fetchCollectionTypes()
    fetchReferringDoctors()
    // Fetch user profile if not already in store
    dispatch(fetchProfileData() as any)
  }, [dispatch])

  const fetchReferringDoctors = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/referring-doctor/getAllBySearch`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      setReferringDoctors(response.data?.data || [])
    } catch (error: any) {
      console.error("Error fetching referring doctors:", error)
      setReferringDoctors([])
    }
  }

  /**
   * Records saved before the Referring Doctor master data existed hold a free-text
   * name with no `referringDrId`. Keep that name selectable so re-saving such a
   * record does not silently reset it to "Self".
   */
  const legacyReferringDoctor = useMemo<ReferringDoctorOption | null>(() => {
    const referredBy = formData?.otherDetails?.referredBy
    if (formData?.otherDetails?.referringDrId || isSelfReferredBy(referredBy)) return null
    const alreadyInMaster = referringDoctors.some(
      (doctor) => doctor.doctorName?.trim().toLowerCase() === referredBy.trim().toLowerCase(),
    )
    if (alreadyInMaster) return null
    return { _id: LEGACY_REFERRING_DOCTOR_ID, doctorName: referredBy }
  }, [formData?.otherDetails?.referredBy, formData?.otherDetails?.referringDrId, referringDoctors])

  const referringDoctorOptions = useMemo<ReferringDoctorOption[]>(
    () => [SELF_REFERRING_DOCTOR, ...(legacyReferringDoctor ? [legacyReferringDoctor] : []), ...referringDoctors],
    [legacyReferringDoctor, referringDoctors],
  )

  const selectedReferringDoctor = useMemo<ReferringDoctorOption>(() => {
    const { referredBy, referringDrId } = formData?.otherDetails || {}
    if (referringDrId) {
      const matchedById = referringDoctors.find((doctor) => doctor._id === referringDrId)
      if (matchedById) return matchedById
    }
    if (isSelfReferredBy(referredBy)) return SELF_REFERRING_DOCTOR
    const matchedByName = referringDoctorOptions.find(
      (doctor) => doctor.doctorName?.trim().toLowerCase() === referredBy.trim().toLowerCase(),
    )
    return matchedByName || { _id: LEGACY_REFERRING_DOCTOR_ID, doctorName: referredBy }
  }, [formData?.otherDetails, referringDoctors, referringDoctorOptions])

  const handleReferringDoctorChange = (doctor: ReferringDoctorOption | null) => {
    // "Self" and legacy free-text entries have no master record, so no id is sent.
    const hasMasterRecord =
      !!doctor && doctor._id !== SELF_REFERRING_DOCTOR_ID && doctor._id !== LEGACY_REFERRING_DOCTOR_ID

    setFormData((prev) => ({
      ...prev,
      otherDetails: {
        ...prev.otherDetails,
        referredBy: doctor?.doctorName || SELF_REFERRING_DOCTOR_LABEL,
        referringDrId: hasMasterRecord ? doctor._id : null,
        referralCommission: hasMasterRecord
          ? Number(doctor?.referralCommission ?? 0)
          : 0,
      },
    }))
  }

  /** Accept a master option or any custom typed name (free-text). */
  const commitReferringDoctorInput = (value: ReferringDoctorOption | string | null) => {
    if (value == null) {
      handleReferringDoctorChange(SELF_REFERRING_DOCTOR)
      return
    }

    if (typeof value !== "string") {
      handleReferringDoctorChange(value)
      return
    }

    const trimmed = value.trim()
    if (!trimmed || trimmed.toLowerCase() === SELF_REFERRING_DOCTOR_LABEL.toLowerCase()) {
      handleReferringDoctorChange(SELF_REFERRING_DOCTOR)
      return
    }

    const matched = referringDoctorOptions.find(
      (doctor) => doctor.doctorName?.trim().toLowerCase() === trimmed.toLowerCase(),
    )
    handleReferringDoctorChange(matched || { _id: LEGACY_REFERRING_DOCTOR_ID, doctorName: trimmed })
  }

  const fetchCollectionTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_LAB_URL}/collection-type/getAllByPagination`, {
        params: { pageNo: 1, count: 100 },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      if (response.data?.isSuccess === true || response.data?.data) {
        setCollectionTypes(response.data?.data || [])
      } else {
        setCollectionTypes(response.data?.data || [])
      }
    } catch (error: any) {
      console.error("Error fetching collection types:", error)
      setCollectionTypes([])
    }
  }

  const handleSearch = () => {
    setOpenSearchList(true)
    setLoading(true)
    let endpoint = ""
    switch (searchType) {
      case "Name":
        endpoint = `searchBy=name&search=${searchValue}`
        break
      case "CNIC":
        endpoint = `searchBy=cnic&search=${searchValue}`
        break
      case "MRN":
        endpoint = `searchBy=mrn&search=${searchValue}`
        break
      case "Phone Number":
        endpoint = `searchBy=mobileNo&search=${searchValue}`
        break
      default:
        console.error("Invalid search type")
        setLoading(false)
        return
    }

    fetch(`${process.env.REACT_APP_LAB_URL}/patient-registration/searchPatient?${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error("Network response was not ok")
        }
      })
      .then((data) => {
        const patients = data?.data
        setSearchResults(patients || [])
        if (searchType === "MRN" && patients && !Array.isArray(patients)) {
          toast.success("Record Found")
        } else if (searchType === "MRN" && !patients) {
          toast.error("Record not Found")
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error)
        toast.error("Search failed")
      })
      .finally(() => setLoading(false))
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSearch()
    }
  }

  const handleKeyPressForCode = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      fetchTestsByCode()
    }
  }

  const handleAutoCompleteKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      fetchTests()
    }
  }

  const handleTestChange = (index: number, field: string, value: any) => {
    setFormData((prev) => {
      const updatedTests = [...(prev.testToPerform || [])]
      if (field === "sampleReceived") {
        updatedTests[index] = {
          ...updatedTests[index],
          sampleReceived: value,
          sampleCollectionDateTime: value
            ? updatedTests[index]?.sampleCollectionDateTime || dayjs().format("YYYY-MM-DDTHH:mm")
            : null,
        }
      } else {
        updatedTests[index] = { ...updatedTests[index], [field]: value }
      }
      return { ...prev, testToPerform: updatedTests }
    })
  }

  const handleRemoveTest = (index: number) => {
    setSelectedTests((prev) => prev.filter((_, i) => i !== index))
    setFormData((prev) => ({
      ...prev,
      testToPerform: (prev.testToPerform || []).filter((_, i) => i !== index),
    }))
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <>
      <Box>
        <Box
          sx={{
            background: "#fff",
            padding: "0.1px 20px",
            borderRadius: "8px",
          }}
        >
          <>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  padding={0}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { md: "18px", xs: "16px" },
                      fontWeight: 600,
                      width: { xs: "100%", md: "auto" },
                      mb: { xs: 1, md: 0 },
                    }}
                  >
                    Registration & Sample
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "48%", md: "auto" },
                      minWidth: { md: "200px" },
                    }}
                  >
                    <CollectionCenterDropdown
                      value={formData.collectionCenterId || ""}
                      onChange={(value: string) =>
                        setFormData({
                          ...formData,
                          collectionCenterId: value,
                        })
                      }
                      error={!!errors.collectionCenterId}
                      helperText={errors.collectionCenterId}
                      required={true}
                      autoSelect={true}
                      saveToStorage={true}
                    />
                  </Box>
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      justifyContent: { xs: "space-between", md: "flex-start" },
                    }}
                  >
                    {/* <Typography sx={{ fontSize: { md: "14px", xs: "12px" } }}>
                      Search By:
                    </Typography> */}
                    <Select
                      className="custom-input"
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      sx={{
                        borderRadius: "8px",
                        background: "#F5F5FB",
                        fontSize: "14px",
                        height: "28px",
                        minWidth: { xs: "80px", sm: "100px" },
                        "& fieldset": { border: "none" },
                      }}
                      input={<OutlinedInput />}
                    >
                      <MenuItem value="MRN">MRN</MenuItem>
                      <MenuItem value="Name">Name</MenuItem>
                      <MenuItem value="CNIC">CNIC</MenuItem>
                      <MenuItem value="Phone Number">Mobile #</MenuItem>
                    </Select>
                    <TextField
                      className="custom-input"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={`Enter ${searchType==='MRN'? 'MRN or Ref MRN':searchType}`}
                      size="small"
                      type={searchType === "CNIC" ? "number" : "text"}
                      sx={{
                        minWidth: { xs: "120px", sm: "150px" },
                        flex: { xs: "1 1 auto", sm: "0 1 auto" },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment  onClick={handleSearch} position="end" sx={{ cursor: "pointer",backgroundColor: "#1976d2",width:"50px", textAlign: "center", borderRadius: "0 8px 8px 0", height: "100%", color:"white" }}>
                            <SearchIcon sx={{ml:"6px"}}  />
                          </InputAdornment>
                        ),
                      }}
                      onKeyPress={handleKeyPress}
                    />
                    <Select
                      className="custom-input"
                      value={selectedPatient?._id || ""}
                      onChange={(e) => {
                        const selected = latestPatient.find((p) => p._id === e.target.value)
                        if (selected) {
                          setSelectedPatient(selected)
                          setFormData((prev) => ({
                            ...prev,
                            patientId: selected._id,
                          }))
                          navigate("/sample-collection", {
                            state: { patient: selected, TestData, edit },
                          })
                        }
                      }}
                      displayEmpty
                      sx={{
                        borderRadius: "8px",
                        background: "#F5F5FB",
                        fontSize: "14px",
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "150px", md: "200px" },
                        height: "30px",
                        "& fieldset": { border: "none" },
                      }}
                      input={<OutlinedInput />}
                    >
                      <MenuItem value="" disabled>
                        --select patient--
                      </MenuItem>
                      {latestPatient.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                          {option.mrn}-{option.patientName}
                        </MenuItem>
                      ))}
                    </Select>
                    <Tooltip title="Add New Patient" placement="top">
                      <Button
                        variant="contained"
                        onClick={handleOpen}
                        disabled={open}
                        size="small"
                        startIcon={<Add />}
                        sx={{
                          minWidth: { xs: "100%", sm: "auto", md: "100px" },
                          width: { xs: "100%", sm: "auto" },
                          mt: { xs: 1, sm: 0 },
                          fontSize: { xs: "12px", md: "12px" },
                          fontWeight: "600",
                          textTransform: "none",
                          borderRadius: "8px",
                          background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                          color: "#ffffff",
                          boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                            boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                            transform: "translateY(-1px)",
                          },
                          "&:disabled": {
                            background: "#b0bec5",
                            color: "#ffffff",
                            opacity: 0.6,
                            cursor: "not-allowed",
                          },
                          "& .MuiButton-startIcon": {
                            marginRight: { xs: "4px", md: "8px" },
                          },
                        }}
                      >
                        Add Patient
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
              <DialogContent>
                <PathologyPatientRegistration closeAllScreen={handleClose} />
              </DialogContent>
            </Dialog>
            <PatientSearchDialog
              loading={loading}
              open={openSearchList}
              searchResults={searchResults.map((patient) => ({
                ...patient,
                mrn: patient.mrn !== undefined ? String(patient.mrn) : "",
              }))}
              onClose={() => setOpenSearchList(false)}
            />
          </>
        </Box>
        {/* Patient Info Section */}
        <Box sx={{ background: "#fff", padding: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            Patient Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                MRN:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.mrn}
                InputProps={{ readOnly: true }}
                sx={{ cursor: "not-allowed" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Patient Name <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.patientName}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Guardian Name <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.guardiansName || "N/A"}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Gender <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.gender}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Date of Birth <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                type="date"
                value={patient?.dob}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Phone Number <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.phonNumber || "N/A"}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                City <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <TextField
                disabled={true}
                className="custom-input"
                fullWidth
                size="small"
                value={patient?.city || "N/A"}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Patient Type <span style={{ color: "red" }}>*</span>:
              </Typography>
              <Select
                className="custom-input"
                fullWidth
                size="small"
                value={formData.patientTypeId || ""}
                onChange={(e) => setFormData({ ...formData, patientTypeId: e.target.value })}
                displayEmpty
                error={!!errors.patientTypeId}
                disabled={isEditMode.current}
              >
                <MenuItem value="" disabled>
                  --select the value--
                </MenuItem>
                {patientType.map((type) => (
                  <MenuItem key={type._id} value={type._id}>
                    {type.patientTypeName}
                  </MenuItem>
                ))}
              </Select>
              {errors.patientTypeId && (
                <Typography variant="caption" color="error" sx={{ fontSize: "10px" }}>
                  {errors.patientTypeId}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Collection Date <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                className="custom-input"
                fullWidth
                size="small"
                type="datetime-local"
                value={formData?.collectionDate && dayjs(formData.collectionDate).format("YYYY-MM-DDTHH:mm")}
                error={!!errors.collectionDate}
                FormHelperTextProps={{
                  sx: { fontSize: "10px" }
                }}
                helperText={errors.collectionDate}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, collectionDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Reporting Date <span style={{ color: "red" }}>*</span>:
              </Typography>
              <TextField
                className="custom-input"
                fullWidth
                size="small"
                type="datetime-local"
                value={formData?.reportingDate && dayjs(formData.reportingDate).format("YYYY-MM-DDTHH:mm")}
                error={!!errors.reportingDate}
                FormHelperTextProps={{
                  sx: { fontSize: "10px" }
                }}
                helperText={errors.reportingDate}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, reportingDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                Other Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                External Reference No <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <TextField
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    otherDetails: {
                      ...prev.otherDetails,
                      externalRefNo: e.target.value,
                    },
                  }))
                }
                className="custom-input"
                fullWidth
                size="small"
                placeholder="External Reference No"
                value={formData?.otherDetails?.externalRefNo || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Specimen Collection Type <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <Select
                className="custom-input"
                fullWidth
                size="small"
                value={formData?.otherDetails?.specimenType || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    otherDetails: {
                      ...prev.otherDetails,
                      specimenType: e.target.value,
                    },
                  }))
                }
                displayEmpty
                input={<OutlinedInput />}
              >
                <MenuItem value="" disabled>
                  --select specimen collection type--
                </MenuItem>
                {collectionTypes && collectionTypes.length > 0 ? (
                  collectionTypes.map((ct: any) => (
                    <MenuItem key={ct._id} value={ct.name}>
                      {ct.name}
                    </MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value="Normal">Normal</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                  </>
                )}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Referred By Dr <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <Autocomplete
                className="custom-input"
                fullWidth
                size="small"
                freeSolo
                disableClearable
                options={referringDoctorOptions}
                value={selectedReferringDoctor}
                onChange={(_event, value) => commitReferringDoctorInput(value)}
                onBlur={(event) => {
                  const typed = (event.target as HTMLInputElement)?.value
                  if (typeof typed === "string") {
                    commitReferringDoctorInput(typed)
                  }
                }}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.doctorName || ""
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box>
                      <Typography sx={{ fontSize: "13px" }}>{option.doctorName}</Typography>
                      {option.specialization && (
                        <Typography sx={{ fontSize: "10px", color: "gray" }}>
                          {option.specialization}
                          {option.hospitalClinic ? ` • ${option.hospitalClinic}` : ""}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Referred By Dr" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Doctor Clinic <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <TextField
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    otherDetails: {
                      ...prev.otherDetails,
                      clinicName: e.target.value,
                    },
                  }))
                }
                className="custom-input"
                fullWidth
                size="small"
                placeholder="Doctor Clinic"
                value={formData?.otherDetails?.clinicName || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Patient Services <span style={{ color: "gray", fontSize: "8px" }}>(optional)</span>:
              </Typography>
              <Select
                className="custom-input"
                fullWidth
                size="small"
                value={formData?.otherDetails?.requestSource || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    otherDetails: {
                      ...prev.otherDetails,
                      requestSource: e.target.value,
                    },
                  }))
                }
                displayEmpty
                input={<OutlinedInput />}
              >
                <MenuItem value="" disabled>
                  --select request source--
                </MenuItem>
                {Object.values(RequestSource).map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ background: "#fff", padding: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            Tests to Perform
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Search and Select the Test:
              </Typography>
              <Tooltip
                title={
                  isEditMode.current && !labPermissions["can_add/remove_tests"]
                    ? "You are not permitted to add or remove tests in edit mode"
                    : ""
                }
                arrow
              >
                <span>
                  <Autocomplete
                    options={testList}
                    getOptionLabel={(option) => option.testName}
                    renderInput={(params) => (
                      <TextField {...params} inputRef={testSearchInputRef} className="custom-input" size="small" fullWidth placeholder="Search test by name" />
                    )}
                    value={null}
                    inputValue={searchQuery}
                    onInputChange={(event, newInputValue) => {
                      setSearchQuery(newInputValue)
                    }}
                    disabled={isEditMode.current && !labPermissions["can_add/remove_tests"]}
                    onKeyPress={handleAutoCompleteKeyPress}
                    onChange={(event, newValue) => {
                      // clear the box and put the cursor back so the next test can be searched right away
                      setSearchQuery("")
                      requestAnimationFrame(() => testSearchInputRef.current?.focus())
                      if (newValue) {
                        setSelectedTests((prev) => {
                          const exists = prev.some((test) => test._id === newValue._id)
                          if (exists) {
                            toast.info(`Test ${newValue.testName} is already added`)
                            return prev
                          }
                          return [...prev, newValue]
                        })

                        setFormData((prev) => {
                          const exists = prev.testToPerform?.some((test) => test.testId === newValue._id)
                          if (exists) {
                            return prev
                          }
                          return {
                            ...prev,
                            testToPerform: [
                              ...(prev.testToPerform || []),
                              {
                                testId: newValue._id,
                                sampleType: "Normal",
                                reportingTime: dayjs()
                                  .add(newValue.ETA?.days || 0, "day")
                                  .add(newValue.ETA?.hrs || 0, "hour")
                                  .add(newValue.ETA?.mins || 0, "minute")
                                  .format("YYYY-MM-DDTHH:mm"),
                                sampleReceived: true,
                                collectionType: "Taken in lab",
                                sampleCollectionDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
                              },
                            ],
                          }
                        })
                      }
                    }}
                  />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#000",
                  fontWeight: "700",
                }}
              >
                Search Test By Code:
              </Typography>
              <Tooltip
                title={
                  isEditMode.current && !labPermissions["can_add/remove_tests"]
                    ? "You are not permitted to add or remove tests in edit mode"
                    : ""
                }
                arrow
              >
                <span>
                  <TextField
                    className="custom-input"
                    size="small"
                    type="number"
                    fullWidth
                    placeholder="Enter test code"
                    value={searchTestByCode}
                    onKeyPress={handleKeyPressForCode}
                    onChange={(e) => setSearchTestByCode(e.target.value)}
                    disabled={isEditMode.current && !labPermissions["can_add/remove_tests"]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, overflowX: "auto", overflowY: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  {[
                    "Code",
                    "Category",
                    "Test",
                    "Specimen",
                    "Collection Type",
                    "Sample Type",
                    "Reporting Time",
                    "Charges (Rs.)",
                    "Received",
                    "",
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      style={{
                        padding: "4px 6px",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: "11px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedTests?.map((t, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "2px 6px", fontSize: "13px" }}>{t?.test?.testCode || t?.testCode}</td>
                    <td style={{ padding: "2px 6px", fontSize: "13px" }}>{t?.test?.category || t?.category}</td>
                    <td style={{ padding: "2px 6px", fontSize: "13px" }}>{t?.test?.testName || t?.testName}</td>
                    <td style={{ padding: "2px 6px", fontSize: "13px" }}>{t?.test?.specimen || t?.specimen}</td>
                    <td style={{ padding: "2px 6px" }}>
                      <Select
                        className="custom-input"
                        size="small"
                        value={
                          formData.testToPerform?.[i]?.collectionType ||
                          selectedTests?.[i]?.collectionType ||
                          "Taken in lab"
                        }
                        onChange={(e) => handleTestChange(i, "collectionType", e.target.value)}
                        sx={{
                          fontSize: "11px",
                          height: "28px",
                          width: "150px",
                        }}
                      >
                        {collectionTypes && collectionTypes.length > 0 ? (
                          collectionTypes.map((ct: any) => (
                            <MenuItem key={ct._id} value={ct.name}>
                              {ct.name}
                            </MenuItem>
                          ))
                        ) : (
                          <>
                            <MenuItem value="Taken in lab">Taken in lab</MenuItem>
                            <MenuItem value="Home collection">Home collection</MenuItem>
                          </>
                        )}
                      </Select>
                    </td>
                    <td style={{ padding: "2px 6px" }}>
                      <Select
                        className="custom-input"
                        size="small"
                        value={formData.testToPerform?.[i]?.sampleType || "Normal"}
                        onChange={(e) => handleTestChange(i, "sampleType", e.target.value)}
                        sx={{ fontSize: "11px", height: "28px" }}
                      >
                        <MenuItem value="Normal">Normal</MenuItem>
                        <MenuItem value="Urgent">Urgent</MenuItem>
                        <MenuItem value="Stat">Stat</MenuItem>
                      </Select>
                    </td>
                    <td style={{ padding: "3px 6px", width: "100px" }}>
                      <TextField
                        className="custom-input"
                        type="datetime-local"
                        size="small"
                        value={
                          formData.testToPerform?.[i]?.reportingTime
                            ? dayjs(formData.testToPerform[i].reportingTime).format("YYYY-MM-DDTHH:mm")
                            : dayjs()
                                .add(t.ETA?.days, "day")
                                .add(t.ETA?.hrs, "hour")
                                .add(t.ETA?.mins, "minute")
                                .format("YYYY-MM-DDTHH:mm")
                        }
                        sx={{ width: "160px" }}
                        onChange={(e) => handleTestChange(i, "reportingTime", e.target.value)}
                      />
                    </td>
                    <td style={{ padding: "2px 6px", fontSize: "13px" }}>
                      {Math.round(t?.test?.testCharges || t?.testCharges || 0)}
                    </td>
                    <td style={{ padding: "2px 6px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={formData.testToPerform?.[i]?.sampleReceived || false}
                        onChange={(e) => handleTestChange(i, "sampleReceived", e.target.checked)}
                        style={{ marginTop: "4px" }}
                      />
                    </td>
                    <td style={{ padding: "2px 6px", textAlign: "center" }}>
                      <Tooltip
                        title={
                          isEditMode.current && !labPermissions["can_add/remove_tests"]
                            ? "You are not permitted to add or remove tests in edit mode"
                            : ""
                        }
                        arrow
                      >
                        <span>
                          <Button
                            color="error"
                            size="small"
                            onClick={() => handleRemoveTest(i)}
                            disabled={isEditMode.current && !labPermissions["can_add/remove_tests"]}
                            sx={{
                              minWidth: "20px",
                              fontSize: "11px",
                              padding: "0px 4px",
                            }}
                          >
                            X
                          </Button>
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {errors.testToPerform && (
              <Typography variant="caption" color="error">
                {errors.testToPerform}
              </Typography>
            )}
          </Box>
          {/* Payment Detail Section */}
          <Box sx={{ background: "#fff", mt: 2 }}>
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
              Payment Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: 2,
              }}
            >
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Gross Amount (Rs.):</Typography>
                <TextField
                  className="custom-input"
                  fullWidth
                  size="small"
                  type="number"
                  disabled={true}
                  value={Math.round(formData.paymentDetail?.grossAmount ?? 0)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      paymentDetail: {
                        grossAmount: Number.parseInt(e.target.value) || 0,
                        discountInPercentage: prev.paymentDetail?.discountInPercentage ?? 0,
                        discountInAmount: prev.paymentDetail?.discountInAmount ?? 0,
                        nowPaying: prev.paymentDetail?.nowPaying ?? 0,
                        dueAmount: prev.paymentDetail?.dueAmount ?? 0,
                      },
                    }))
                  }
                  inputProps={{ step: 1 }}
                  sx={paymentFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Discount %:</Typography>
                <Tooltip
                  title={isEditMode.current&&!labPermissions.can_give_discount ? "You are not permitted to give discount" : ""}
                  arrow
                >
                  <span>
                    <TextField
                      className="custom-input"
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="0"
                      disabled={isEditMode.current&&!labPermissions.can_give_discount}
                      value={
                        formData.paymentDetail?.discountInPercentage === 0
                          ? ""
                          : Math.floor(formData.paymentDetail?.discountInPercentage ?? 0)
                      } // Floor to remove decimals in display
                      onChange={(e) => {
                        let percentage = Number.parseInt(e.target.value) || 0 // Use parseInt to force integer
                        if (isNaN(percentage)) percentage = 0
                        percentage = Math.min(100, Math.max(0, percentage)) // Cap at 0-100
                        setFormData((prev) => ({
                          ...prev,
                          paymentDetail: {
                            ...prev.paymentDetail!,
                            discountInPercentage: percentage,
                          },
                        }))
                      }}
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1, // Reinforce step
                      }}
                      onInput={(e) => {
                        // Additional validation to strip decimals on input
                        const target = e.target as HTMLInputElement
                        if (target.value.includes(".")) {
                          target.value = Math.floor(Number.parseFloat(target.value)).toString()
                          // Trigger change event manually
                          target.dispatchEvent(new Event("change", { bubbles: true }))
                        }
                      }}
                      sx={paymentFieldSx}
                    />
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Discount Amount (Rs.):</Typography>
                <Tooltip
                  title={isEditMode.current&&!labPermissions.can_give_discount ? "You are not permitted to give discount" : ""}
                  arrow
                >
                  <span>
                    <TextField
                      className="custom-input"
                      fullWidth
                      disabled={isEditMode.current&&!labPermissions.can_give_discount}
                      size="small"
                      type="number"
                      placeholder="0"
                      value={
                        formData.paymentDetail?.discountInAmount === 0
                          ? ""
                          : Math.round(formData.paymentDetail?.discountInAmount ?? 0)
                      }
                      onChange={(e) => {
                        const amount = Number.parseInt(e.target.value) || 0
                        handleDiscountAmountChange(amount)
                      }}
                      inputProps={{ min: 0, step: 1 }}
                      sx={paymentFieldSx}
                    />
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Net Amount (Rs.):</Typography>
                <TextField
                  className="custom-input"
                  disabled={true}
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="0"
                  value={Math.round(formData.paymentDetail?.netAmount || 0)}
                  onChange={(e) => {
                    const amount = Number.parseInt(e.target.value) || 0
                    handleDiscountAmountChange(amount)
                  }}
                  inputProps={{ min: 0 }}
                  sx={paymentFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Now Paying (Rs.):</Typography>
                <Tooltip
                  title={isEditMode.current&&!labPermissions.can_receive_due_payment ? "You are not permitted to recieved due payment" : ""}
                  arrow
                >
                <TextField
                  className="custom-input"
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="0"
                  disabled={isEditMode.current&&!labPermissions.can_receive_due_payment}
                  value={
                    formData.paymentDetail?.nowPaying === 0 ? "" : Math.round(formData.paymentDetail?.nowPaying ?? 0)
                  }
                  onChange={(e) => {
                    const amount = Number.parseInt(e.target.value) || 0
                    handleNowPayingChange(amount)
                  }}
                  inputProps={{ min: 0 }}
                  sx={paymentFieldSx}
                />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700" }}>Due Amount (Rs.):</Typography>
                <TextField
                  className="custom-input"
                  disabled={true}
                  fullWidth
                  size="small"
                  type="number"
                  value={Math.round(formData.paymentDetail?.dueAmount || 0)}
                  InputProps={{ readOnly: true }}
                  sx={{
                    ...paymentFieldSx,
                    "& .MuiInputBase-input": {
                      ...paymentFieldSx["& .MuiInputBase-input"],
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Grid>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small" // ✅ make it smaller
              disabled={BillPrintLoading}
              color="primary"
              onClick={() => (edit ? handleUpdateTestPrintBill("a4") : handleSaveTestAndPrint("a4"))}
              sx={{
                mt: 1, // less top margin
                px: 1.5, // reduce horizontal padding
                py: 0.5, // reduce vertical padding
                fontSize: "0.75rem", // smaller font
                minWidth: "auto", // removes default min width
              }}
            >
              {BillPrintLoading && printFormat === "a4" ? (
                <CircularProgress size={18} /> // smaller spinner
              ) : (
                `${edit ? "Update and print Invoice" : "Save and print Invoice"}`
              )}
            </Button>

            <Button
              variant="contained"
              size="small"
              disabled={BillPrintLoading}
              color="primary"
              onClick={() => (edit ? handleUpdateTestPrintBill("thermal") : handleSaveTestAndPrint("thermal"))}
              sx={{
                mt: 1,
                px: 1.5,
                py: 0.5,
                fontSize: "0.75rem",
                minWidth: "auto",
              }}
            >
              {BillPrintLoading && printFormat === "thermal" ? (
                <CircularProgress size={18} />
              ) : (
                `${edit ? "Update" : "Save"} & Thermal Print`
              )}
            </Button>

            <Button
              variant="contained"
              size="small" // ✅ make it smaller
              disabled={loading}
              color="primary"
              onClick={edit ? handleUpdateTest : handleSaveTest}
              sx={{
                mt: 1,
                px: 1.5,
                py: 0.5,
                fontSize: "0.75rem",
                minWidth: "auto",
              }}
            >
              {loading ? <CircularProgress size={18} /> : `${edit ? "update" : "Save"} Test`}
            </Button>
          </Box>
        </Box>
      </Box>
      {openPrintDialogPayment &&
        billData &&
        (printFormat === "thermal" ? (
          <ThermalBillReport
            open={openPrintDialogPayment}
            data={billData}
            onClose={() => {
              setOpenPrintDialogPayment(false)
              navigate("/sample")
            }}
          />
        ) : (
          <BillReport
            open={openPrintDialogPayment}
            data={billData}
            onClose={() => {
              setOpenPrintDialogPayment(false)
              navigate("/sample")
            }}
          />
        ))}
    </>
  )
}

export default CollectionForm